// Excel export of the SCI list and its history (/api/companies/{id}/sci-exports).
// The exported table is sent gzipped to the API, which keeps it: the same file can be rebuilt later.

import ExcelJS from 'exceljs/dist/exceljs.min.js';

import type { SCIListItem } from '../../../../@types/SCI';
import { parseLotsRange, parseSciListState } from './sciListQuery';

export type SciExportCell = string | number | null;

export interface SciExportContent {
  headers: string[];
  rows: SciExportCell[][];
}

export interface SciExportHistoryItem {
  id: number;
  campaign: boolean;
  name: string | null;
  filters: Record<string, string>;
  rowCount: number;
  fileSize: number;
  createdAt: string | null;
  createdBy: { firstname: string | null; lastname: string | null; email: string } | null;
  canDelete?: boolean;
}

export const getSciDisplayName = (sci: SCIListItem): string => {
  const isDefined = (v?: string | null) => !!v && v !== '[ND]';
  if (isDefined(sci.denominationUniteLegale)) {
    return sci.denominationUniteLegale as string;
  }
  if (isDefined(sci.prenom1UniteLegale) && isDefined(sci.nomUniteLegale)) {
    return `${sci.prenom1UniteLegale as string} ${sci.nomUniteLegale as string}`;
  }
  if (isDefined(sci.denominationUsuelleEtablissement)) {
    return sci.denominationUsuelleEtablissement as string;
  }
  if (isDefined(sci.name)) {
    return sci.name as string;
  }
  return `SCI ${sci.siren}`;
};

export const SCI_EXPORT_HEADERS = [
  'Identifiant RNIC',
  'Nom immeuble',
  'Adresse immeuble',
  'Dénomination SCI',
  'SIREN de la SCI',
  'Adresse du siège de la SCI',
  'Civilité contact',
  'Nom contact',
  'Prénom contact',
  'Téléphone contact',
  'Email contact',
  'Adresse contact',
  'Lots dans la copro',
  'Total lots SCI',
  'Contact qualifié',
  'Cible Gest Loc',
  'Cible Transac',
  // Read back by the BO "Import contacts SCI": Oui / Non, empty when not answered
  'Client en gestion',
];

export const buildSciExportContent = (rows: SCIListItem[]): SciExportContent => ({
  headers: SCI_EXPORT_HEADERS,
  rows: rows.map((r) => {
    const primaryProp = r.properties?.find((p) => p && p.copro);
    const copro = r.primaryCopro || primaryProp?.copro;
    const siegeAddress = [
      r.numeroVoieEtablissement || '',
      r.indiceRepetitionEtablissement || '',
      r.typeVoieEtablissement || '',
      r.libelleVoieEtablissement || '',
    ]
      .filter((part) => !!part && String(part).trim() !== '')
      .join(' ')
      .replaceAll(/\s+/g, ' ')
      .trim();
    const contact = r.qualification?.contact || {};
    const alreadyManaged = r.qualification?.alreadyManaged;

    return [
      copro?.remoteId || '',
      copro?.name || '',
      copro?.address || '',
      getSciDisplayName(r),
      r.siren,
      siegeAddress,
      contact?.civilite || '',
      contact?.nom || '',
      contact?.prenom || '',
      contact?.telephone || '',
      contact?.email || '',
      contact?.adresse || '',
      r.stats.propertiesWithinCopro,
      r.stats.totalProperties,
      r.qualification?.contactQualified ? 'Oui' : 'Non',
      r.qualification?.targetedGestion ? 'Oui' : 'Non',
      r.qualification?.targetedTransaction ? 'Oui' : 'Non',
      alreadyManaged === true ? 'Oui' : (alreadyManaged === false ? 'Non' : ''),
    ];
  }),
});

const slug = (value: string): string => value
  .normalize('NFD')
  .replaceAll(/[̀-ͯ]/g, '')
  .replaceAll(/[^A-Za-z0-9]+/g, '-')
  .replaceAll(/^-+|-+$/g, '')
  .slice(0, 60);

const pad = (value: number): string => String(value).padStart(2, '0');

/** SCIs_<campagne>_<date>.xlsx, or SCIs_export_<date>.xlsx outside a campaign, in local time as in the history. */
export const sciExportFileName = (name: string | null, date: Date): string => {
  const stamp = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}_${pad(date.getHours())}h${pad(date.getMinutes())}`;
  const label = name ? slug(name) : '';

  return `SCIs_${label || 'export'}_${stamp}.xlsx`;
};

export const downloadSciExportWorkbook = async(content: SciExportContent, fileName: string): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('SCIs');
  worksheet.addRow(content.headers);
  content.rows.forEach((row) => worksheet.addRow(row));
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  window.setTimeout(() => window.URL.revokeObjectURL(link.href), 1000);
};

/** Body of the history upload: gzipped JSON, or plain JSON where CompressionStream is missing (Safari < 16.4). */
export const encodeExportContent = async(value: unknown): Promise<{ blob: Blob; filename: string }> => {
  const json = new Blob([JSON.stringify(value)], { type: 'application/json' });
  if (typeof CompressionStream === 'undefined') {
    return { blob: json, filename: 'export.json' };
  }
  const blob = await new Response(json.stream().pipeThrough(new CompressionStream('gzip'))).blob();

  return { blob, filename: 'export.json.gz' };
};

const YES_NO: Record<string, string> = { yes: 'Oui', no: 'Non' };
const MANAGED: Record<string, string> = { yes: 'Oui', no: 'Non (ou non renseigné)', unknown: 'Non renseigné' };

const lotsLabel = (label: string, min: string, max: string): string | null => {
  const range = parseLotsRange(min, max);
  if (range.min !== null && range.max !== null) { return `${label} : de ${range.min} à ${range.max}`; }
  if (range.min !== null) { return `${label} : ${range.min} ou plus`; }
  if (range.max !== null) { return `${label} : ${range.max} ou moins`; }

  return null;
};

/** Readable filters of an export, from the list URL parameters it was made with. */
export const describeSciExportFilters = (filters: Record<string, string>): string[] => {
  const state = parseSciListState(new URLSearchParams(filters));
  const labels: string[] = [];
  if (state.qualified !== 'all') { labels.push(`Contact qualifié : ${YES_NO[state.qualified]}`); }
  if (state.targeted !== 'all') { labels.push(`Cible Gest Loc : ${YES_NO[state.targeted]}`); }
  if (state.targetedTransaction !== 'all') { labels.push(`Cible Transac : ${YES_NO[state.targetedTransaction]}`); }
  if (state.alreadyManaged !== 'all') { labels.push(`Client en gestion : ${MANAGED[state.alreadyManaged]}`); }
  const lotsCopro = lotsLabel('Lots dans copro', state.lotsCoproMin, state.lotsCoproMax);
  if (lotsCopro) { labels.push(lotsCopro); }
  const lotsTotal = lotsLabel('Total lots SCI', state.lotsTotalMin, state.lotsTotalMax);
  if (lotsTotal) { labels.push(lotsTotal); }
  if (state.search) { labels.push(`Recherche : « ${state.search} »`); }

  return labels;
};
